/**
 * PortalInviteForm — the 1-click portal invite, extended for Bereich 6.
 *
 * The 1-click invite invites the customer's PRIMARY holder (grant_id NULL). In
 * `gruppenbasiert` mode the backend BLOCKS the invite unless the customer has >=1
 * Berechtigungsgruppe (400 `portal_groups_required`) and unless the broker picked at least
 * one (400 `portal_groups_selection_required`); the chosen ids go WITH the invite POST, so
 * the assignment is server-side and atomic with it. In `einfach` mode the group picker is
 * hidden and the invite behaves as before. Each 400 is surfaced as a helpful hint.
 *
 * "E-Mail speichern" only stores the address. It invites nobody, and on an activated holder
 * the server refuses to change it (409 `portal_holder_active`) so the holder keeps their login.
 *
 * [Source: apps/api/src/modules/customer-portal/portal-invite.service.ts] (_assertGroupModeReady)
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Send, UserPlus } from 'lucide-react';
import { Button, TextInput } from '../../administration/components';
import { ApiError, apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import type { PortalGroupDto, PortalVisibilityMode } from '../types';

interface Props {
  subjectType: string;
  subjectId: string;
  mode: PortalVisibilityMode;
  /** All groups (the system group is filtered out — it is not user-assignable). */
  gruppen: PortalGroupDto[];
  onFeedback?: (msg: string) => void;
}

/** Turn an invite error into a broker-readable message (the group-gate gets a clear hint). */
function inviteErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.code === 'portal_groups_required') {
    return 'Bitte legen Sie zuerst mindestens eine Berechtigungsgruppe an. Im gruppenbasierten Modus kann sonst kein Portal-Nutzer eingeladen werden.';
  }
  if (e instanceof ApiError && e.code === 'portal_groups_selection_required') {
    return 'Bitte wählen Sie mindestens eine Berechtigungsgruppe für den neuen Portal-Nutzer.';
  }
  if (e instanceof ApiError && e.code === 'portal_holder_active') {
    return 'Der Portal-Zugang ist bereits aktiviert. Die Login-Adresse kann nicht mehr geändert werden.';
  }
  if (e instanceof ApiError && e.code === 'validation_failed') {
    return 'Es ist keine E-Mail-Adresse hinterlegt. Bitte speichern Sie zuerst eine Einladungs-E-Mail.';
  }
  return e instanceof Error ? e.message : 'Einladung fehlgeschlagen.';
}

export function PortalInviteForm({ subjectType, subjectId, mode, gruppen, onFeedback }: Props) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const assignable = gruppen.filter((g) => !g.istSystem);
  const groupModeReady = mode !== 'gruppenbasiert' || selectedGroups.length > 0;

  const invalidateAfterInvite = () => {
    void qc.invalidateQueries({ queryKey: qk.adminPortalGrants(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: qk.adminPortalAccess(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: qk.portalGruppen(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: qk.portalVisibilityMode(subjectType, subjectId) });
  };

  const setInviteEmail = useMutation({
    mutationFn: () => apiClient.admin.setPortalInviteEmail(subjectType, subjectId, email.trim()),
    onSuccess: () => {
      setError(null);
      onFeedback?.('Einladungs-E-Mail gespeichert.');
      invalidateAfterInvite();
    },
    onError: (e: unknown) => setError(inviteErrorMessage(e)),
  });

  const sendInvite = useMutation({
    // 1-click invite of the primary holder; the chosen groups ride along, so the server
    // assigns them in the same transaction (no bypassable post-hoc member loop).
    mutationFn: () => apiClient.admin.sendPortalInvite(subjectType, subjectId, selectedGroups),
    onSuccess: () => {
      setError(null);
      onFeedback?.('Einladung ausgelöst.');
      setSelectedGroups([]);
      invalidateAfterInvite();
    },
    onError: (e: unknown) => setError(inviteErrorMessage(e)),
  });

  const toggleGroup = (id: string) =>
    setSelectedGroups((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <UserPlus className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Portal-Nutzer einladen</h3>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[16rem] flex-1">
          <label htmlFor="invite-email" className="mb-1 block text-xs text-muted-foreground">
            Einladungs-E-Mail
          </label>
          <TextInput
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kunde@beispiel.ch"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!email.trim() || setInviteEmail.isPending}
          onClick={() => setInviteEmail.mutate()}
        >
          E-Mail speichern
        </Button>
      </div>

      {mode === 'gruppenbasiert' && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3">
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Ein neuer Portal-Nutzer braucht mindestens eine Gruppe. Nur so ist die
              kategoriebasierte Sichtbarkeit gesetzt.
            </span>
          </div>
          {assignable.length === 0 ? (
            <p className="text-xs text-status-danger">
              Es gibt noch keine Gruppe. Legen Sie zuerst oben eine Berechtigungsgruppe an.
            </p>
          ) : (
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label="Gruppen für den neuen Portal-Nutzer"
            >
              {assignable.map((g) => {
                const on = selectedGroups.includes(g.id);
                return (
                  <label
                    key={g.id}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/60"
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleGroup(g.id)}
                      className="size-3.5 rounded border-border"
                      aria-label={g.name}
                    />
                    <span className="text-foreground">{g.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        {error != null ? (
          <span className="text-xs text-status-danger">{error}</span>
        ) : (
          <span />
        )}
        <Button
          size="sm"
          disabled={sendInvite.isPending || !groupModeReady}
          onClick={() => sendInvite.mutate()}
        >
          <Send className="size-4" /> Einladung senden
        </Button>
      </div>
    </div>
  );
}
